import { Share2 } from 'lucide-react';
import { useState } from 'react';

const copyToClipboard = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const ProfileShareButton = ({
  title = 'KalaManch profile',
  text = 'View this profile on Kalamanch.art',
  url,
  className = '',
}) => {
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(false);

  const shareUrl = url || window.location.href;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(`${text} ${shareUrl}`);
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const isIos = /iPad|iPhone|iPod/.test(userAgent) || (navigator?.platform === 'MacIntel' && navigator?.maxTouchPoints > 1);
  const isInstagramFriendlyFallback = isIos;
  const shareLinks = [
    { label: 'WhatsApp', href: `https://wa.me/?text=${encodedText}` },
    { label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
    { label: 'X', href: `https://twitter.com/intent/tweet?text=${encodedText}` },
    { label: 'Telegram', href: `https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(text)}` },
  ];

  const showTemporaryStatus = (message) => {
    setStatus(message);
    window.setTimeout(() => setStatus(''), 3000);
  };

  const handleInstagramShare = async () => {
    try {
      await copyToClipboard(shareUrl);
      setOpen(false);
      showTemporaryStatus('Link copied. Instagram is opening — paste the link in story, bio, or DM.');
      window.location.href = 'instagram://app';
      window.setTimeout(() => {
        window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
      }, 900);
    } catch (error) {
      setOpen(false);
      showTemporaryStatus('Could not prepare Instagram share');
    }
  };

  const handleShare = async () => {
    setStatus('');

    try {
      if (navigator.share && !isInstagramFriendlyFallback) {
        await navigator.share({ title, text, url: shareUrl });
        return;
      }

      setOpen((current) => !current);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      setOpen(true);
    }
  };

  const handleCopy = async () => {
    try {
      await copyToClipboard(shareUrl);
      showTemporaryStatus('Link copied');
    } catch (error) {
      showTemporaryStatus('Could not copy link');
    }
  };

  return (
    <div className={`relative flex flex-col gap-2 ${className}`}>
      <button type="button" className="btn-outline inline-flex items-center justify-center gap-2 w-full sm:w-auto" onClick={handleShare}>
        <Share2 className="h-4 w-4 shrink-0" aria-hidden="true" />Share Profile
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-2 w-56 rounded-2xl border border-orange-100 bg-white p-3 shadow-xl">
          <p className="px-2 pb-2 text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Share on</p>
          <div className="grid gap-2">
            <button
              type="button"
              onClick={handleInstagramShare}
              className="rounded-xl px-3 py-2 text-left text-sm font-bold text-pink-700 hover:bg-pink-50"
            >
              Instagram
            </button>
            {shareLinks.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-xl px-3 py-2 text-sm font-bold text-gray-800 hover:bg-orange-50"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </a>
            ))}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                handleCopy();
              }}
              className="rounded-xl px-3 py-2 text-left text-sm font-bold text-[#FF6F00] hover:bg-orange-50"
            >
              Copy Link
            </button>
          </div>
        </div>
      )}
      {status && <span className="text-xs font-medium text-[#00796B]">{status}</span>}
    </div>
  );
};

export default ProfileShareButton;
